import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageContainer } from '@/components/layout';

type Platform = 'cafe24' | 'makeshop' | 'shopify';

const platformConfig: Record<Platform, {
  label: string;
  color: string;
  activeColor: string;
  popupImg: string;
  usageImg: string;
  usageTitle: string;
  usageSteps: string[];
}> = {
  cafe24: {
    label: 'Cafe24',
    color: 'border-gray-200 text-gray-600 hover:bg-gray-50',
    activeColor: 'border-blue-500 text-blue-600 bg-blue-50',
    popupImg: '/shop_ai_alt_generator2.png',
    usageImg: '/shop_ai_alt_generator3.png',
    usageTitle: '상품 등록 페이지에서 사용하기',
    usageSteps: [
      'Cafe24 관리자 > 상품 등록/수정 페이지로 이동',
      '페이지 새로고침 (F5) - 확장 프로그램 설치 후 필수',
      '상품명과 상품 이미지 입력',
      '"ALT 생성" 버튼 클릭',
      'AI가 자동으로 ALT 텍스트 생성 및 입력',
    ],
  },
  makeshop: {
    label: 'Make#',
    color: 'border-gray-200 text-gray-600 hover:bg-gray-50',
    activeColor: 'border-orange-500 text-orange-600 bg-orange-50',
    popupImg: '/shop_ai_alt_generator4.png',
    usageImg: '/shop_ai_alt_generator5.png',
    usageTitle: '상품 등록 페이지에서 사용하기',
    usageSteps: [
      'Make# 관리자 > 상품 등록/수정 페이지로 이동',
      '페이지 새로고침 (F5) - 확장 프로그램 설치 후 필수',
      '상품명과 상품 이미지 입력',
      '"ALT 생성" 버튼 클릭',
      'AI가 자동으로 ALT 텍스트 생성 및 입력',
    ],
  },
  shopify: {
    label: 'Shopify',
    color: 'border-gray-200 text-gray-600 hover:bg-gray-50',
    activeColor: 'border-green-500 text-green-600 bg-green-50',
    popupImg: '/shop_ai_alt_generator6.png',
    usageImg: '/shop_ai_alt_generator7.png',
    usageTitle: '제품 등록 페이지에서 사용하기',
    usageSteps: [
      'Shopify 관리자 > 제품 등록/수정 페이지로 이동',
      '제품 이미지 썸네일 클릭',
      '이미지 상세 패널이 열리면 "ALT 생성" 버튼 클릭',
      'AI가 자동으로 ALT 텍스트 생성 및 입력',
    ],
  },
};

const ZoomableImg: React.FC<{ src: string; alt: string; caption: string; onZoom: (src: string) => void }> = ({ src, alt, caption, onZoom }) => (
  <div className="my-4">
    <img
      src={src}
      alt={alt}
      onClick={() => onZoom(src)}
      className="w-full max-w-[800px] mx-auto rounded-lg shadow-md border border-gray-200 cursor-zoom-in hover:opacity-90 transition-opacity"
    />
    <p className="text-center text-sm text-gray-500 mt-2">{caption} <span className="text-xs text-blue-400">(클릭하면 크게 보기)</span></p>
  </div>
);

const isPlatform = (value: string): value is Platform =>
  value === 'cafe24' || value === 'makeshop' || value === 'shopify';

export const ShopExtensionGuide: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Platform>('cafe24');
  const [enlargedImg, setEnlargedImg] = useState<string | null>(null);
  const config = platformConfig[activeTab];
  const usageSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (isPlatform(hash)) {
      setActiveTab(hash);
      usageSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const handleTabClick = (key: Platform) => {
    setActiveTab(key);
    window.history.replaceState(null, '', `#${key}`);
  };

  return (
    <PageContainer maxWidth="full">
      <div className="text-center mb-8">
        <img 
          src="/shop_ai_alt_generator1.png" 
          alt="Shop AI ALT Text Generator" 
          className="mx-auto rounded-lg shadow-md mb-4 w-[440px] h-[280px] object-cover"
        />
        <h1 className="text-3xl font-bold text-gray-900 border-b-3 border-blue-500 pb-3 mb-4">
          Shop AI ALT Text Generator - 확장 프로그램 사용 가이드
        </h1>
      </div>
      
      <p className="text-gray-700 mb-8 text-lg">
        Shop AI ALT Text Generator는 쇼핑몰 상품 등록 페이지에서 AI를 사용하여 이미지 ALT 텍스트를 자동으로 생성하는 Chrome 확장 프로그램입니다. 
        웹 접근성을 향상하고 SEO를 최적화하여 더 많은 고객에게 도달할 수 있도록 도와드립니다.
      </p>

      <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border-l-4 border-cyan-500 p-5 rounded-r-lg mb-8">
        <h3 className="font-bold text-lg mb-3 text-gray-800">✨ 확장 프로그램 특징</h3>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>쇼핑몰 상품 등록 페이지에서 자동 ALT 텍스트 생성</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>AI 기반 고품질 ALT 텍스트 생성</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>컨텍스트 프로필 지원 (상품명 및 페이지 컨텍스트 고려)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>다양한 쇼핑몰 플랫폼 지원 (Cafe24, Make#, Shopify)</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>JPG, PNG, WebP, GIF, SVG, AVIF, TIFF 등 다양한 이미지 형식 지원</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-cyan-600 mt-1">✓</span>
            <span>간편한 버튼 클릭으로 ALT 텍스트 생성</span>
          </li>
        </ul>
      </div>

      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-6">📦 1. 설치 방법</h2>
          
          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">Chrome Web Store에서 설치</h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700">Chrome 브라우저에서 <a href="https://chromewebstore.google.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Chrome Web Store</a> 접속</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">검색창에 "Shop AI ALT Text Generator" 검색</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <span className="text-gray-700">"Chrome에 추가" 버튼 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">4</span>
                <span className="text-gray-700">권한 요청 팝업에서 "확장 프로그램 추가" 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">5</span>
                <span className="text-gray-700">설치 완료! 🎉</span>
              </li>
            </ol>
          </div>

          <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
            <p className="font-semibold text-green-800">💡 설치 확인</p>
            <p className="text-green-700 text-sm mt-1">설치가 완료되면 브라우저 우측 상단 퍼즐 아이콘을 클릭하여 "Shop AI ALT Text Generator" 아이콘이 표시되는지 확인하세요.</p>
          </div>
          <ZoomableImg src="/shop_ai_alt_generator8.png" alt="Chrome 툴바의 Shop AI ALT Text Generator 아이콘" caption="Chrome 툴바에서 확장프로그램 아이콘 확인" onZoom={setEnlargedImg} />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-6">🔑 2. API 키 발급</h2>
          
          <p className="text-gray-700 mb-4">확장 프로그램을 사용하기 위해 먼저 API 키를 발급받아야 합니다.</p>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700">대시보드에서 <Link to="/dashboard/ApiKeyManagement" className="text-blue-600 hover:underline font-semibold">"API 키 관리"</Link> 메뉴 선택</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">"새 API 키 생성" 버튼 클릭</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <span className="text-gray-700">API 키 이름 입력 (예: Chrome Extension)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold">4</span>
                <span className="text-gray-700">생성된 API 키 복사</span>
              </li>
            </ol>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg">
            <p className="font-semibold text-yellow-800">⚠️ 중요</p>
            <p className="text-yellow-700 text-sm mt-1">API 키는 안전하게 보관하세요. 유출되지 않도록 주의해야 합니다.</p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-6">⚙️ 3. 확장 프로그램 설정</h2>

          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">3.1 API 키 입력</h3>
          
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <ol className="space-y-4">
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">1</span>
                <span className="text-gray-700">Chrome 브라우저 우측 상단 퍼즐 아이콘 클릭 → "Shop AI ALT Text Generator" 선택</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">2</span>
                <span className="text-gray-700">"API Key" 입력란에 발급받은 API 키 붙여넣기</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="flex-shrink-0 w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">3</span>
                <span className="text-gray-700">"저장" 버튼 클릭 → "설정이 저장되었습니다." 알림 확인</span>
              </li>
            </ol>
          </div>

          <h3 className="text-xl font-semibold text-gray-700 mt-6 mb-4">3.2 컨텍스트 프로필 설정 (선택사항)</h3>
          
          <div className="bg-cyan-50 border-l-4 border-cyan-500 p-4 rounded-r-lg">
            <p className="font-semibold text-gray-800 mb-2">🎯 컨텍스트 프로필 사용</p>
            <ul className="space-y-1 text-gray-700 text-sm ml-4">
              <li>• "컨텍스트 프로필 사용" 체크박스 활성화 (기본 체크)</li>
              <li>• 상품명과 페이지 컨텍스트를 고려하여 더 정확한 ALT 텍스트 생성</li>
              <li>• 체크 해제 시 일반적인 ALT 텍스트가 생성됩니다</li>
            </ul>
          </div>
        </section>

        <section ref={usageSectionRef}>
          <h2 className="text-2xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2 mb-6">🚀 4. 쇼핑몰별 사용 방법</h2>

          <div className="flex border-b border-gray-200 mb-6">
            {(Object.keys(platformConfig) as Platform[]).map((key) => (
              <button
                key={key}
                onClick={() => handleTabClick(key)}
                className={`px-6 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                  activeTab === key ? platformConfig[key].activeColor : platformConfig[key].color
                }`}
              >
                {platformConfig[key].label}
              </button>
            ))}
          </div>

          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-700 mb-4">확장프로그램 열기</h3>
              <ZoomableImg src={config.popupImg} alt={`${config.label} 확장프로그램 팝업`} caption={`${config.label} - 확장프로그램 팝업 화면`} onZoom={setEnlargedImg} />
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-700 mb-4">{config.usageTitle}</h3>
              <div className="bg-white border border-gray-200 rounded-lg p-6 mb-4">
                <ol className="space-y-4">
                  {config.usageSteps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-gray-700">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <ZoomableImg src={config.usageImg} alt={`${config.label} ${config.usageTitle}`} caption={`${config.label} - ${config.usageTitle}`} onZoom={setEnlargedImg} />
            </div>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 rounded-r-lg mt-6">
            <p className="font-semibold text-yellow-800">⚠️ 주의사항</p>
            <ul className="space-y-1 text-yellow-700 text-sm ml-4 mt-1">
              <li>• 확장 프로그램 설치 후 반드시 페이지 새로고침 (F5) 해야 버튼이 나타납니다</li>
              <li>• 버튼이 보이지 않으면 페이지 새로고침을 시도하세요</li>
              <li>• 다시 페이지를 열면 버튼이 자동으로 생성됩니다</li>
            </ul>
          </div>
        </section>

        <section className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">💡 팁</h2>
          <ul className="space-y-2 text-gray-700">
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>Cafe24, Make#, Shopify 쇼핑몰을 지원합니다</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>컨텍스트 프로필을 사용하면 상품명을 고려하여 더 정확한 ALT 텍스트가 생성됩니다</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>상품 등록 페이지와 상품 수정 페이지 모두 지원됩니다</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>문제가 발생하면 확장 프로그램을 다시 로드해 보세요</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-1">•</span>
              <span>향후 더 많은 쇼핑몰 플랫폼이 추가될 예정입니다</span>
            </li>
          </ul>
        </section>
      </div>
      {enlargedImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setEnlargedImg(null)}
        >
          <div className="relative max-w-[95vw] max-h-[95vh]">
            <img
              src={enlargedImg}
              alt="확대 이미지"
              className="max-w-full max-h-[95vh] rounded-lg shadow-2xl object-contain"
            />
            <button
              onClick={() => setEnlargedImg(null)}
              className="absolute top-2 right-2 w-8 h-8 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-gray-700 font-bold shadow"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </PageContainer>
  );
};
